import {
  glidePosition,
  startGlide,
  settledGlide,
  metersBetween,
  type EntityGlide,
} from './entityGlide.js';
import { isResumeFrame } from './pageResume.js';

/** Per-feed motion tuning. */
export interface GlideTrackerConfig {
  /** Glide window in ms — keep just over the feed's poll cadence so markers never freeze. */
  glideMs: number;
  /** Jumps beyond this (m) teleport instead of gliding (trip reassign / GPS reacquire). */
  snapMeters: number;
  /** Sub-GPS-noise wiggle (m) to ignore so parked/docked vehicles sit still. */
  jitterMeters: number;
}

/** One reported entity for this frame: a stable id and its latest [lng,lat]. */
export interface GlideFeature {
  id: string;
  position: [number, number];
  /** Optional previous [lng,lat] for an entity the tracker has not seen before, so
   * it can start gliding `seedFrom → position` on the FIRST frame instead of settling
   * in place and waiting a full poll to observe motion. Both points are real reported
   * positions — this is seeded interpolation, not extrapolation. */
  seedFrom?: [number, number];
}

/** What to draw for one entity this frame. */
export interface GlideSample {
  /** Interpolated [lng,lat] to draw right now. */
  position: [number, number];
  /** Straight-line distance (m) the entity moved on its most recent fresh poll —
   * the underway-vs-stationary signal. Persists between polls; ~0 for docked. */
  movedMeters: number;
  /** The active glide (from → to over the window), for layers that want to draw a
   * trail along the entity's recent track. */
  glide: EntityGlide;
}

export interface GlideTracker {
  /**
   * Advance every tracked entity to this frame and return its {@link GlideSample},
   * keyed by id. Entities absent from `features` are pruned. `generatedAt` detects a
   * fresh snapshot so glides restart only when new positions arrive, not on every
   * animation frame.
   */
  step(features: GlideFeature[], generatedAt: number, nowMs: number): Map<string, GlideSample>;
}

interface TrackedEntity {
  glide: EntityGlide;
  movedMeters: number;
}

/**
 * Create an id-keyed entity-interpolation tracker. Tracking by id (not buffer
 * index) means a vehicle always glides toward *its own* next position, so the set
 * of vehicles changing between polls never makes one marker tween across the map
 * toward another's spot. State is closed over per instance — no module globals, so
 * each layer owns its own tracker.
 */
export function createGlideTracker(cfg: GlideTrackerConfig): GlideTracker {
  let displayed = new Map<string, TrackedEntity>();
  // Detect a fresh snapshot (generatedAt advanced) so glides are only restarted
  // when new positions actually arrive, not on every animation frame.
  let lastGeneratedAt = 0;

  return {
    step(features, generatedAt, nowMs) {
      const isFreshPoll = generatedAt !== lastGeneratedAt;
      if (isFreshPoll) lastGeneratedAt = generatedAt;

      // First frame back from a hidden tab: snap every entity to its current report.
      // We can't lean on time-clamping here — while the tab was hidden rAF was
      // suspended and the intermediate polls were dropped, so a glide's `from` is the
      // minutes-old position, not just a stale clock. Without this, the marker would
      // animate all that piled-up movement over one glide window and streak across
      // the map. (Ordinary mid-session stalls — map drag, GC — are NOT snapped: there
      // glidePosition resumes at the time-correct point and snapMeters teleports big
      // jumps, so a settle-on-stall would needlessly freeze every marker.)
      const resumed = isResumeFrame();

      const next = new Map<string, TrackedEntity>();
      const samples = new Map<string, GlideSample>();

      for (const f of features) {
        const prev = displayed.get(f.id);
        // On resume, settle in place but keep the prior movement classification so a
        // moving vehicle doesn't flash as stationary for one poll.
        const tracked = resumed
          ? { glide: settledGlide(f.position, nowMs, cfg.glideMs), movedMeters: prev?.movedMeters ?? 0 }
          : nextEntity(cfg, prev, f, isFreshPoll, nowMs);
        next.set(f.id, tracked);
        samples.set(f.id, {
          position: glidePosition(tracked.glide, nowMs),
          movedMeters: tracked.movedMeters,
          glide: tracked.glide,
        });
      }

      displayed = next; // prunes entities that dropped out of the feed
      return samples;
    },
  };
}

/**
 * Decide the glide + recorded movement for one entity this frame:
 *  - first sight → settle on the reported spot, no movement yet
 *  - same snapshot (just animating) → keep the in-progress glide and last movement
 *  - fresh snapshot → record the actual per-poll displacement, then:
 *      · jitter-sized move → keep gliding (don't restart on GPS noise)
 *      · big jump → snap (trip reassignment / GPS reacquire)
 *      · real move → glide from the current marker spot to the report
 *
 * `movedMeters` is recorded on EVERY fresh poll (even jitter-sized), so a vehicle
 * that arrives and stops drops to ~0 movement immediately instead of inheriting its
 * last big glide's span — which keeps the underway/stationary classification honest.
 */
function nextEntity(
  cfg: GlideTrackerConfig,
  prev: TrackedEntity | undefined,
  feature: GlideFeature,
  isFreshPoll: boolean,
  nowMs: number,
): TrackedEntity {
  const reported = feature.position;
  if (!prev) {
    // First sight: if the feed handed us where this entity was last poll, start
    // gliding from there so it moves immediately; otherwise settle in place.
    const seed = feature.seedFrom;
    if (seed && metersBetween(seed, reported) > cfg.jitterMeters) {
      return {
        glide: startGlide(seed, reported, nowMs, cfg.glideMs),
        movedMeters: metersBetween(seed, reported),
      };
    }
    return { glide: settledGlide(reported, nowMs, cfg.glideMs), movedMeters: 0 };
  }
  if (!isFreshPoll) return prev;

  const gap = metersBetween(prev.glide.to, reported);
  if (gap <= cfg.jitterMeters) return { glide: prev.glide, movedMeters: gap };
  if (gap > cfg.snapMeters) {
    return { glide: settledGlide(reported, nowMs, cfg.glideMs), movedMeters: gap };
  }
  return {
    glide: startGlide(glidePosition(prev.glide, nowMs), reported, nowMs, cfg.glideMs),
    movedMeters: gap,
  };
}
